import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Trash2, Edit, Save, X, Image, ChevronDown, ChevronUp, Eye, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_PASSWORD = 'lionheart2024';

// Available icons for models
const ICON_OPTIONS = [
  'Dumbbell', 'Palmtree', 'Users', 'Beef', 'Waves', 'Sparkles', 'Flame', 'Crown', 'Heart', 'Star'
];

// Available gradient presets
const GRADIENT_OPTIONS = [
  { label: 'Orange to Red', value: 'from-orange-500 to-red-500' },
  { label: 'Emerald to Teal', value: 'from-emerald-500 to-teal-600' },
  { label: 'Pink to Rose', value: 'from-pink-500 to-rose-600' },
  { label: 'Amber to Yellow', value: 'from-amber-600 to-yellow-700' },
  { label: 'Cyan to Sky', value: 'from-cyan-500 to-sky-600' },
  { label: 'Blue to Indigo', value: 'from-blue-500 to-indigo-600' },
  { label: 'Violet to Purple', value: 'from-violet-500 to-purple-600' },
  { label: 'Lime to Green', value: 'from-lime-500 to-green-600' },
  { label: 'Red to Pink', value: 'from-red-500 to-pink-600' },
  { label: 'Fuchsia to Purple', value: 'from-fuchsia-500 to-purple-600' },
];

const LollipopAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingModel, setEditingModel] = useState(null);
  const [expandedModel, setExpandedModel] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // New model form state
  const [newModel, setNewModel] = useState({
    id: '',
    name: '',
    type: '',
    tagline: '',
    icon: 'Dumbbell',
    gradient: 'from-orange-500 to-red-500',
    images: ['', '', '', '', ''],
    content: [
      { id: 1, title: '', type: '' },
      { id: 2, title: '', type: '' },
      { id: 3, title: '', type: '' },
      { id: 4, title: '', type: '' },
      { id: 5, title: '', type: '' },
    ]
  });

  // Fetch models on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchModels();
    }
  }, [isAuthenticated]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/lollipop/models`);
      const data = await res.json();
      setModels(data.models || []);
    } catch (error) {
      toast.error('Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to Lollipop Admin!');
    } else {
      toast.error('Invalid password');
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!newModel.id || !newModel.name) {
      toast.error('Model ID and Name are required');
      return;
    }
    
    // Filter out empty images
    const filteredImages = newModel.images.filter(img => img.trim() !== '');
    if (filteredImages.length === 0) {
      toast.error('At least one image is required');
      return;
    }
    
    // Filter out empty content
    const filteredContent = newModel.content.filter(c => c.title.trim() !== '');
    
    const modelToCreate = {
      ...newModel,
      images: filteredImages,
      content: filteredContent.map((c, idx) => ({ ...c, id: idx + 1 }))
    };
    
    try {
      const res = await fetch(`${API_URL}/api/lollipop/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': ADMIN_PASSWORD
        },
        body: JSON.stringify(modelToCreate)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`${newModel.name} created successfully!`);
        setShowAddForm(false);
        resetNewModel();
        fetchModels();
      } else {
        toast.error(data.detail || 'Failed to create model');
      }
    } catch (error) {
      toast.error('Failed to create model');
    }
  };

  const handleUpdateModel = async (modelId, updatedData) => {
    try {
      const res = await fetch(`${API_URL}/api/lollipop/models/${modelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': ADMIN_PASSWORD
        },
        body: JSON.stringify(updatedData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`${updatedData.name} updated successfully!`);
        setEditingModel(null);
        fetchModels();
      } else {
        toast.error(data.detail || 'Failed to update model');
      }
    } catch (error) {
      toast.error('Failed to update model');
    }
  };

  const handleDeleteModel = async (modelId, modelName) => {
    if (!window.confirm(`Are you sure you want to delete ${modelName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/lollipop/models/${modelId}`, {
        method: 'DELETE',
        headers: {
          'x-admin-password': ADMIN_PASSWORD
        }
      });
      
      if (res.ok) {
        toast.success(`${modelName} deleted`);
        fetchModels();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete model');
      }
    } catch (error) {
      toast.error('Failed to delete model');
    }
  };

  const resetNewModel = () => {
    setNewModel({
      id: '',
      name: '',
      type: '',
      tagline: '',
      icon: 'Dumbbell',
      gradient: 'from-orange-500 to-red-500',
      images: ['', '', '', '', ''],
      content: [
        { id: 1, title: '', type: '' },
        { id: 2, title: '', type: '' },
        { id: 3, title: '', type: '' },
        { id: 4, title: '', type: '' },
        { id: 5, title: '', type: '' },
      ]
    });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-slate-900 to-black flex items-center justify-center p-4">
        <Helmet>
          <title>Lollipop Admin - Login</title>
        </Helmet>
        
        <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md border border-slate-700">
          <h1 className="text-3xl font-black text-center mb-2 bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
            My Boy Lollipop
          </h1>
          <p className="text-slate-400 text-center mb-8">Admin Panel</p>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 mb-4"
            />
            <button
              type="submit"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold hover:opacity-90 transition"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-900 to-black text-white p-4 sm:p-8">
      <Helmet>
        <title>Lollipop Admin - Manage Models</title>
      </Helmet>
      
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
              Lollipop Admin
            </h1>
            <p className="text-slate-400">Manage your AI models</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to="/myboylollipop"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
            >
              <Eye className="w-4 h-4" />
              View Site
            </Link>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg font-semibold hover:opacity-90 transition"
            >
              <Plus className="w-5 h-5" />
              Add Model
            </button>
          </div>
        </div>
      </div>

      {/* Models List */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-400 mt-4">Loading models...</p>
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700">
            <p className="text-slate-400 mb-4">No models yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg font-semibold"
            >
              Add Your First Model
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {models.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                isExpanded={expandedModel === model.id}
                isEditing={editingModel === model.id}
                onToggleExpand={() => setExpandedModel(expandedModel === model.id ? null : model.id)}
                onEdit={() => setEditingModel(model.id)}
                onCancelEdit={() => setEditingModel(null)}
                onSave={(updatedData) => handleUpdateModel(model.id, updatedData)}
                onDelete={() => handleDeleteModel(model.id, model.name)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Model Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-2xl my-8 border border-slate-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Add New Model</h2>
              <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateModel} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Model ID (slug)</label>
                  <input
                    type="text"
                    value={newModel.id}
                    onChange={(e) => setNewModel({ ...newModel, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="e.g., jake"
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={newModel.name}
                    onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                    placeholder="e.g., Jake"
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Type</label>
                  <input
                    type="text"
                    value={newModel.type}
                    onChange={(e) => setNewModel({ ...newModel, type: e.target.value })}
                    placeholder="e.g., Fitness Model"
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Tagline</label>
                  <input
                    type="text"
                    value={newModel.tagline}
                    onChange={(e) => setNewModel({ ...newModel, tagline: e.target.value })}
                    placeholder="e.g., Gym Life • Your Fantasy"
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Icon</label>
                  <select
                    value={newModel.icon}
                    onChange={(e) => setNewModel({ ...newModel, icon: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  >
                    {ICON_OPTIONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Color Theme</label>
                  <select
                    value={newModel.gradient}
                    onChange={(e) => setNewModel({ ...newModel, gradient: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  >
                    {GRADIENT_OPTIONS.map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Images */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Image URLs (paste image links)
                </label>
                {newModel.images.map((img, idx) => (
                  <input
                    key={idx}
                    type="url"
                    value={img}
                    onChange={(e) => {
                      const newImages = [...newModel.images];
                      newImages[idx] = e.target.value;
                      setNewModel({ ...newModel, images: newImages });
                    }}
                    placeholder={`Image ${idx + 1} URL`}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white mb-2"
                  />
                ))}
                <p className="text-xs text-slate-500">Upload images to your hosting and paste the URLs here</p>
              </div>
              
              {/* Content Titles */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Content Titles (for gallery cards)</label>
                {newModel.content.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={c.title}
                      onChange={(e) => {
                        const newContent = [...newModel.content];
                        newContent[idx] = { ...newContent[idx], title: e.target.value };
                        setNewModel({ ...newModel, content: newContent });
                      }}
                      placeholder={`Content ${idx + 1} title`}
                      className="px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                    />
                    <input
                      type="text"
                      value={c.type}
                      onChange={(e) => {
                        const newContent = [...newModel.content];
                        newContent[idx] = { ...newContent[idx], type: e.target.value };
                        setNewModel({ ...newModel, content: newContent });
                      }}
                      placeholder="Type (e.g., Exclusive)"
                      className="px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                    />
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 py-3 rounded-xl bg-slate-700 text-white font-semibold hover:bg-slate-600 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold hover:opacity-90 transition"
                >
                  Create Model
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Model Card Component
const ModelCard = ({ model, isExpanded, isEditing, onToggleExpand, onEdit, onCancelEdit, onSave, onDelete }) => {
  const [editData, setEditData] = useState(model);
  
  useEffect(() => {
    setEditData(model);
  }, [model, isEditing]);

  const handleSave = () => {
    onSave(editData);
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header Row */}
      <div className="flex items-center gap-4 p-4">
        {/* Thumbnail */}
        <img
          src={model.images[0]}
          alt={model.name}
          className="w-16 h-16 rounded-lg object-cover"
        />
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-white">{model.name}</h3>
          <p className="text-slate-400 text-sm truncate">{model.type} • {model.tagline}</p>
        </div>
        
        {/* Color Preview */}
        <div className={`w-20 h-8 rounded-lg bg-gradient-to-r ${model.gradient}`}></div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={onEdit}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                title="Edit"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/30 rounded-lg transition"
                title="Save"
              >
                <Save className="w-5 h-5" />
              </button>
              <button
                onClick={onCancelEdit}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                title="Cancel"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={onToggleExpand}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-700 p-4 space-y-4">
          {isEditing ? (
            // Edit Mode
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Type</label>
                  <input
                    type="text"
                    value={editData.type}
                    onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Tagline</label>
                <input
                  type="text"
                  value={editData.tagline}
                  onChange={(e) => setEditData({ ...editData, tagline: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Icon</label>
                  <select
                    value={editData.icon}
                    onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  >
                    {ICON_OPTIONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Color Theme</label>
                  <select
                    value={editData.gradient}
                    onChange={(e) => setEditData({ ...editData, gradient: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  >
                    {GRADIENT_OPTIONS.map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">Images</label>
                {editData.images.map((img, idx) => (
                  <input
                    key={idx}
                    type="url"
                    value={img}
                    onChange={(e) => {
                      const newImages = [...editData.images];
                      newImages[idx] = e.target.value;
                      setEditData({ ...editData, images: newImages });
                    }}
                    placeholder={`Image ${idx + 1} URL`}
                    className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white mb-2"
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setEditData({ ...editData, images: [...editData.images, ''] })}
                  className="text-sm text-pink-400 hover:text-pink-300"
                >
                  + Add another image
                </button>
              </div>
            </>
          ) : (
            // View Mode
            <>
              <div>
                <p className="text-sm text-slate-400 mb-2">Images ({model.images.length})</p>
                <div className="flex gap-2 flex-wrap">
                  {model.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${model.name} ${idx + 1}`}
                      className="w-20 h-24 rounded-lg object-cover"
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-slate-400 mb-2">Content ({model.content?.length || 0})</p>
                <div className="flex gap-2 flex-wrap">
                  {model.content?.map((c, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-700 rounded-full text-sm">
                      {c.title} <span className="text-slate-400">({c.type})</span>
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="pt-2 border-t border-slate-700">
                <p className="text-xs text-slate-500">
                  Icon: {model.icon} | Gradient: {model.gradient}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LollipopAdmin;
